import { useRef, useState, type FormEvent } from 'react';
import Modal, { inputCls, labelCls } from '@/components/base/Modal';
import {
  saveExpense,
  updateExpense,
  uploadExpenseReceipt,
  deleteExpenseReceipt,
  EXPENSE_CATEGORIES,
  type Expense,
} from '@/services/expenses';

interface ExpenseModalProps {
  expense?: Expense | null;
  onClose: () => void;
  onSaved: () => void;
}

const categoryKeys = Object.keys(EXPENSE_CATEGORIES);

export default function ExpenseModal({ expense, onClose, onSaved }: ExpenseModalProps) {
  const isEditing = !!expense;

  const [category, setCategory] = useState(expense?.category ?? 'operation');
  const [description, setDescription] = useState(expense?.description ?? '');
  const [amount, setAmount] = useState(expense?.amount != null ? String(expense.amount) : '');
  const [expenseDate, setExpenseDate] = useState(() =>
    expense?.expenseDate
      ? new Date(expense.expenseDate).toISOString().slice(0, 10)
      : new Date().toISOString().slice(0, 10),
  );
  const [receiptUrl, setReceiptUrl] = useState(expense?.receiptUrl ?? '');
  const [fileName, setFileName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadedRef = useRef<string[]>([]);
  const originalReceiptRef = useRef<string>(expense?.receiptUrl ?? '');
  const savedRef = useRef(false);

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');

    const { url, error: upErr } = await uploadExpenseReceipt(file);
    setUploading(false);

    if (upErr) {
      setError(`Error al subir "${file.name}": ${upErr}`);
      return;
    }
    if (url) {
      uploadedRef.current.push(url);
      setReceiptUrl(url);
      setFileName(file.name);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  // Quita el comprobante solo de la vista local. El borrado real en Storage se
  // ejecuta al guardar, para que "cancelar" no deje comprobantes huérfanos.
  function removeFile() {
    setReceiptUrl('');
    setFileName('');
  }

  // Cierra el modal. Si no se guardó, limpia los comprobantes subidos en esta
  // sesión para no dejar huérfanos en Storage.
  async function handleClose() {
    if (!savedRef.current) {
      for (const url of uploadedRef.current) {
        await deleteExpenseReceipt(url);
      }
    }
    onClose();
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (!description.trim()) {
      setError('La descripción es obligatoria.');
      return;
    }
    if ((Number(amount) || 0) <= 0) {
      setError('El monto debe ser mayor a cero.');
      return;
    }

    const input = {
      category,
      description,
      amount: Number(amount) || 0,
      expenseDate: expenseDate ? new Date(expenseDate + 'T12:00:00').toISOString() : '',
      receiptUrl,
    };

    setSaving(true);
    let saveError: string | null;
    if (isEditing && expense) {
      ({ error: saveError } = await updateExpense(expense.id, input));
    } else {
      ({ error: saveError } = await saveExpense(input));
    }
    setSaving(false);

    if (saveError) {
      setError(saveError);
      return;
    }

    // Limpia comprobantes de Storage que quedaron sin uso (quitados con la "X"
    // o reemplazados por una URL pegada). Se conserva el que quedó final.
    const finalUrl = receiptUrl.trim();
    if (isEditing && originalReceiptRef.current && originalReceiptRef.current !== finalUrl) {
      await deleteExpenseReceipt(originalReceiptRef.current);
    }
    for (const url of uploadedRef.current) {
      if (url !== finalUrl) {
        await deleteExpenseReceipt(url);
      }
    }

    savedRef.current = true;
    onSaved();
    onClose();
  }

  return (
    <Modal
      title={isEditing ? 'Editar Gasto' : 'Nuevo Gasto'}
      subtitle={isEditing ? `Editando: ${expense?.description}` : 'Registra un gasto operativo.'}
      onClose={handleClose}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2.5">
            <i className="ri-error-warning-line"></i>
            <span>{error}</span>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Categoría</label>
            <select className={inputCls} value={category} onChange={(e) => setCategory(e.target.value)}>
              {categoryKeys.map((c) => (
                <option key={c} value={c}>{EXPENSE_CATEGORIES[c]}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Monto ($) *</label>
            <input type="number" min="0" className={inputCls} value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
        </div>
        <div>
          <label className={labelCls}>Descripción *</label>
          <input className={inputCls} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Energía del mes, cajas de empaque..." />
        </div>
        <div>
          <label className={labelCls}>Fecha</label>
          <input type="date" className={inputCls} value={expenseDate} onChange={(e) => setExpenseDate(e.target.value)} />
        </div>

        <div>
          <label className={labelCls}>Comprobante / Soporte</label>

          {fileName ? (
            <div className="flex items-center gap-2 rounded-md border border-background-200 bg-background-50 px-3 py-2.5">
              <i className="ri-file-text-line text-accent-600 text-lg"></i>
              <span className="flex-1 text-sm text-foreground-800 truncate">{fileName}</span>
              <button
                type="button"
                onClick={removeFile}
                className="w-7 h-7 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-200 hover:text-red-600 transition-colors cursor-pointer"
                aria-label="Quitar comprobante"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
          ) : (
            <>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleFile}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="inline-flex items-center gap-2 text-sm px-4 py-2.5 border border-dashed border-background-300 text-foreground-700 rounded-lg hover:bg-background-50 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {uploading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>Subiendo...
                  </>
                ) : (
                  <>
                    <i className="ri-upload-cloud-2-line"></i>Subir comprobante
                  </>
                )}
              </button>
              <p className="text-xs text-foreground-400 mt-1.5">
                Opcional. Se sube a Storage y se guarda la URL pública. También puedes pegar una URL abajo.
              </p>
            </>
          )}
        </div>

        <div>
          <label className={labelCls}>URL soporte</label>
          <input className={inputCls} value={receiptUrl} onChange={(e) => { setReceiptUrl(e.target.value); setFileName(''); }} placeholder="https://..." />
        </div>

        <div className="flex gap-3 pt-1">
          <button type="button" onClick={handleClose} className="flex-1 px-4 py-2.5 text-sm border border-background-300 text-foreground-700 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer">
            Cancelar
          </button>
          <button type="submit" disabled={saving} className="flex-1 px-4 py-2.5 text-sm font-semibold bg-accent-500 text-white rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed">
            {saving ? (<><i className="ri-loader-4-line animate-spin mr-1.5"></i>Guardando...</>) : (<><i className="ri-save-line mr-1.5"></i>{isEditing ? 'Guardar cambios' : 'Crear gasto'}</>)}
          </button>
        </div>
      </form>
    </Modal>
  );
}